import { ReactNode } from "react";
import { useIntl } from "react-intl";

import { ReactComponent as SingleOrder } from "assets/images/icons/SingleOrder.svg";
import { ReactComponent as ConsolidatedOrder } from "assets/images/icons/ConsolidatedOrder.svg";
import { ReactComponent as OCRIcon } from "assets/images/icons/OCR.svg";
import Badge from "components/v2/Badge/Badge";

export type RequestSelectionType = {
  title: string;
  description: string;
  buttonLabel: string;
  onClickPath: string;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  badge?: ReactNode;
};

interface UseGetRequestSelectionOptionsProps {
  showOCRSelection: boolean;
  showConsolidationSelection: boolean;
}

export default function useGetRequestSelectionOptions({
  showOCRSelection,
  showConsolidationSelection,
}: UseGetRequestSelectionOptionsProps) {
  const intl = useIntl();

  const getQuoteRequestSelectionOptions: RequestSelectionType[] = [
    {
      title: intl.formatMessage({
        id: "requestSelectionModal.quoteRequest.manualRequest",
        defaultMessage: "Manual request",
      }),
      description: intl.formatMessage({
        id: "requestSelectionModal.quoteRequest.manualRequest.description",
        defaultMessage: "Create a quote request using the standard request form",
      }),
      buttonLabel: intl.formatMessage({
        id: "requestSelectionModal.quoteRequest.manualRequest.button",
        defaultMessage: "Add manually",
      }),
      onClickPath: "/quote-request",
      icon: SingleOrder,
    },
    ...(showOCRSelection
      ? [
          {
            title: intl.formatMessage({
              id: "requestSelectionModal.quoteRequest.uploadAndChill",
              defaultMessage: "Upload and chill",
            }),
            description: intl.formatMessage({
              id: "requestSelectionModal.quoteRequest.uploadAndChill.description",
              defaultMessage: "Generate a draft quote request by converting an existing packing list",
            }),
            buttonLabel: intl.formatMessage({
              id: "requestSelectionModal.quoteRequest.uploadAndChill.button",
              defaultMessage: "Upload document",
            }),
            onClickPath: "/document-parser?request_type=shipment",
            icon: OCRIcon,
            badge: (
              <div className="ml-xs">
                <Badge label="BETA" color="purple" type="status-badge" />
              </div>
            ),
          },
        ]
      : []),
    ...(showConsolidationSelection
      ? [
          {
            title: intl.formatMessage({
              id: "requestSelectionModal.quoteRequest.consolidateRequest",
              defaultMessage: "Consolidate request",
            }),
            description: intl.formatMessage({
              id: "requestSelectionModal.quoteRequest.consolidateRequest.description",
              defaultMessage: "Combine existing quick requests into a single quote request",
            }),
            buttonLabel: intl.formatMessage({
              id: "requestSelectionModal.quoteRequest.consolidateRequest.button",
              defaultMessage: "Start consolidating",
            }),
            onClickPath: "/order-consolidation",
            icon: ConsolidatedOrder,
          },
        ]
      : []),
  ];

  const getQuickRequestSelectionOptions: RequestSelectionType[] = [
    {
      title: intl.formatMessage({
        id: "requestSelectionModal.quickRequest.manualRequest",
        defaultMessage: "Manual request",
      }),
      description: intl.formatMessage({
        id: "requestSelectionModal.quickRequest.manualRequest.description",
        defaultMessage: "Create a quick request using the standard request form",
      }),
      buttonLabel: intl.formatMessage({
        id: "requestSelectionModal.quickRequest.manualRequest.button",
        defaultMessage: "Add manually",
      }),
      onClickPath: "/quick-request",
      icon: SingleOrder,
    },
    ...(showOCRSelection
      ? [
          {
            title: intl.formatMessage({
              id: "requestSelectionModal.quickRequest.uploadAndChill",
              defaultMessage: "Upload and chill",
            }),
            description: intl.formatMessage({
              id: "requestSelectionModal.quickRequest.uploadAndChill.description",
              defaultMessage: "Generate a draft quick request by converting an existing packing list",
            }),
            buttonLabel: intl.formatMessage({
              id: "requestSelectionModal.quickRequest.uploadAndChill.button",
              defaultMessage: "Upload document",
            }),
            onClickPath: "/document-parser?request_type=quickrequest",
            icon: OCRIcon,
            badge: (
              <div className="ml-xs">
                <Badge label="BETA" color="purple" type="status-badge" />
              </div>
            ),
          },
        ]
      : []),
  ];

  return { getQuoteRequestSelectionOptions, getQuickRequestSelectionOptions };
}
